import React, { useState, useCallback, useMemo, useRef, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Container, Table, Button, Row, Col, Spinner, Dropdown, Form } from 'react-bootstrap';
import { UploadModal, InfoModal, CommentModal } from './components/modal/modal';
import ReportsService from '../../../../../../services/reports-service';
import { IdentityContext } from '../../../../../../context/identity-context';

const IssuerAdhocReportContainer = ({ items }) => {
  const { user } = useContext(IdentityContext);
  const reportService = useMemo(() => new ReportsService(), []);

  // State to control modals and report sending
  const [uploadModalData, setUploadModalData] = useState({ show: false, report: null, item: null });
  const [selectedItems, setSelectedItems] = useState([]);
  const [sendingReports, setSendingReports] = useState(false);
  const [sendModalData, setSendModalData] = useState({ show: false, message: '' });
  const [commentModalData, setCommentModalData] = useState({ show: false, comment: '' });

  // State and ref for dropdown functionality
  const [expandedItem, setExpandedItem] = useState(null);
  const dropdownRefs = useRef([]);

  // New search term state for filtering table rows
  const [searchTerm, setSearchTerm] = useState('');

  // 1) Keep track of original index when filtering
  const filteredWithIndex = useMemo(() => {
    return items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => {
        const searchableText = `
          ${item.reportTitle} 
          ${item.securityIssuer} 
          ${item.bpId} 
          ${item.securityName} 
          ${item.isin} 
          ${item.issuerRequestDate} 
          ${item.taxAdjustmentType}
          ${item.amount || ''} 
          ${item.status}
        `.toLowerCase();
        return searchableText.includes(searchTerm.toLowerCase());
      });
  }, [items, searchTerm]);

  const handleUploadClick = useCallback((item) => {
    setUploadModalData({ show: true, report: null, item });
  }, []);

  const handleUploadModalClose = useCallback(() => {
    setUploadModalData({ show: false, report: null, item: null });
  }, []);

  const handleCheckboxChange = (originalIndex) => (e) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, originalIndex]);
    } else {
      setSelectedItems((prev) => prev.filter((i) => i !== originalIndex));
    }
  };

  const handlePreviewClick = async (item) => {
    try {
      const options = {};
      if (item.bpId) options.bpId = item.bpId;
      if (item.taxAdjustmentType) options.taxAdjustmentType = item.taxAdjustmentType;
      
      const response = await reportService.retrieveIssuerAdhocReportFile(
        item.reportType,
        item.isin,
        item.reportTitle,
        item.paymentDate,
        options
      );
      
      if (response && response.fileURL) {
        const newWindow = window.open(response.fileURL, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      } else {
        console.error('Preview: No file URL returned');
      }
    } catch (error) {
      console.error('Error fetching report preview:', error.message);
    }
  };

  const handleDownloadClick = async (item) => {
    try {
      const options = {};
      if (item.bpId) options.bpId = item.bpId;
      if (item.taxAdjustmentType) options.taxAdjustmentType = item.taxAdjustmentType;
      
      const response = await reportService.retrieveIssuerAdhocReportFile(
        item.reportType,
        item.isin,
        item.reportTitle,
        item.paymentDate,
        options
      );
      
      if (response && response.fileURL) {
        const url = response.fileURL;
        const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1);

        const fileResponse = await fetch(url);
        if (!fileResponse.ok) throw new Error('Network response was not ok');

        const blob = await fileResponse.blob();
        const objectUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        console.error('Download: No file URL returned');
      }
    } catch (error) {
      console.error('Error fetching report download:', error);
    }
  };

  const handleDropdownToggle = (index) => {
    setExpandedItem((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRefs.current.some(ref => ref && ref.contains(event.target))) return;
      if (document.getElementById('dropdown-menu') && document.getElementById('dropdown-menu').contains(event.target)) return;
      setExpandedItem(null);
    };

    if (expandedItem !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedItem]);

  const handleCommentClick = (comment) => {
    setCommentModalData({ show: true, comment });
  };

  const handleCommentModalClose = () => {
    setCommentModalData({ show: false, comment: '' });
  };

  const renderStatusCell = (item, index) => {
    if (item.status === 'Report Unavailable') {
      return (
        <Button variant="primary" onClick={() => handleUploadClick(item)}>
          Upload
        </Button>
      );
    } else {
      return (
        <div className="status-container" ref={el => (dropdownRefs.current[index] = el)}>
          <span className="status-text">{item.status}</span>
          <Dropdown show={expandedItem === index}>
            <Dropdown.Toggle
              variant="link"
              className="dropdown-arrow-button"
              onClick={() => handleDropdownToggle(index)}
            >
              ▼
            </Dropdown.Toggle>
            {expandedItem === index &&
              ReactDOM.createPortal(
                <div className="dropdown-overlay">
                  <Dropdown.Menu className="dropdown-menu" id="dropdown-menu">
                    <Dropdown.Item onClick={() => { handlePreviewClick(item); setExpandedItem(null); }}>
                      Preview
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => { handleDownloadClick(item); setExpandedItem(null); }}>
                      Download
                    </Dropdown.Item>
                    {(item.status === 'Report Available'  || item.status === 'For Adjustment') && (
                      <Dropdown.Item onClick={() => { handleUploadClick(item); setExpandedItem(null); }}>
                        Upload
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </div>,
                document.body
              )}
          </Dropdown>
        </div>
      );
    }
  };

  const handleSendClick = () => {
    setSendingReports(true);
    const selectedCount = selectedItems.length;
    let successCount = 0;

    if (selectedCount > 0) {
      const sendPromises = selectedItems.map((index) => {
        const item = items[index];
        if (['Report Available', 'For Adjustment'].includes(item.status)) {
          return reportService
            .caMakerSendIssuerAdhocReports(
              item.isin,
              item.bpId,
              item.reportTitle,
              item.taxAdjustmentType,
              item.paymentDate,
              user.attributes['custom:display_name'],
            )
            .then(() => {
              successCount++;
            })
            .catch((error) => {
              console.error('Error sending report:', error);
            });
        }
        return Promise.resolve();
      });

      Promise.all(sendPromises).then(() => {
        setSendingReports(false);
        const message =
          successCount === selectedCount
            ? `Reports Successfully Sent\n${selectedCount} reports have been successfully sent.`
            : `Some reports were not sent. Successfully sent ${successCount} out of ${selectedCount}.`;
        setSendModalData({ show: true, message });

        if (successCount === selectedCount) {
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      });
    } else {
      setSendingReports(false);
      setSendModalData({
        show: true,
        message: 'No Reports Sent\nThere are no reports sent for selected items.',
      });
    }
  };

  const handleSendModalClose = () => {
    setSendModalData({ show: false, message: '' });
    setSendingReports(false);
    setSelectedItems([]);
  };

  return (
    <Container
      fluid
      className="issuer-adhoc-report-container report-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '37rem',
      }}
    >
      <Row className="mb-3">
        <Col md={12}>
          <Form.Control
            type="text"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Table bordered responsive>
          <thead>
            <tr>
              <th>REPORT NAME</th>
              <th>ISSUER</th>
              <th>BPID</th>
              <th>SECURITY NAME</th>
              <th>ISIN</th>
              <th>REQUESTED DATE</th>
              <th>Refund Amount</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {filteredWithIndex.length > 0 ? (
              filteredWithIndex.map(({ item, originalIndex }, rowIndex) => (
                <tr key={originalIndex}>
                  <td>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        style={{ marginRight: '8px' }}
                        disabled={!(item.status === 'Report Available' || item.status === 'For Adjustment')}
                        checked={selectedItems.includes(originalIndex)}
                        onChange={handleCheckboxChange(originalIndex)}
                      />
                      {item.reportTitle}
                      {item.taxAdjustmentType && `(${item.taxAdjustmentType})`}
                    </label>
                  </td>
                  <td>{item.securityIssuer}</td>
                  <td>{item.bpId}</td>
                  <td>
                    {item.securityName}
                    {item.comment && item.comment.trim() !== '' && (
                        <span
                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                          onClick={() => handleCommentClick(item.comment)}
                        >
                          &#9865;
                        </span>
                      )}
                  </td>
                  <td>{item.isin}</td>
                  <td>{item.issuerRequestDate}</td>
                  <td>{item.amount || ''}</td>
                  <td>{renderStatusCell(item, originalIndex)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center">
                  No Issuer Adhoc Reports Available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button variant="primary" onClick={handleSendClick} disabled={sendingReports}>
            {sendingReports ? <Spinner animation="border" style={{ width: '20px', height: '20px' }} /> : 'Send Report'}
          </Button>
        </Col>
      </Row>

      <UploadModal
        report={uploadModalData.report}
        item={uploadModalData.item}
        show={uploadModalData.show}
        onClose={handleUploadModalClose}
      />
      <InfoModal
        show={sendModalData.show}
        title="Send Report"
        message={sendModalData.message}
        onClose={handleSendModalClose}
      />
      <CommentModal
        show={commentModalData.show}
        comment={commentModalData.comment}
        onClose={handleCommentModalClose}
      />
    </Container>
  );
};

export default IssuerAdhocReportContainer;
